'use client';

import { useState } from 'react';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { BAR_CAPSULE, segmentPill } from '@/components/ui/bar';
import type { BarAsk } from '@/lib/stores/bar-ask.store';
import { useBarPanel } from './use-bar-panel';

/**
 * A page's one ask, in Sign in's slot. For a signed-out visitor the pill
 * becomes the sign-in form the way `SignInPill` does, with the ask as its
 * button and the claim as what a verified code runs; for a signed-in one the
 * pill runs the claim itself. Not ready yet: the pill stays and a line under
 * it says what the page is waiting for.
 */
export function AskPill({ ask, signedIn }: { ask: BarAsk; signedIn: boolean }) {
  const { open, setOpen, wrap } = useBarPanel();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    const failure = await ask.run();
    if (failure) {
      // The closed pill runs the claim without opening; the message lives in the panel.
      setError(failure);
      setBusy(false);
      setOpen(true);
    }
  };

  if (!open) {
    return (
      <div className={BAR_CAPSULE}>
        <button
          type="button"
          onClick={() => (signedIn && ask.ready ? void run() : setOpen(true))}
          aria-expanded={false}
          disabled={busy}
          className={segmentPill(true)}
        >
          {busy ? 'Making it yours' : ask.label}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={wrap}
      className="mm-unfold pointer-events-auto flex w-[19rem] origin-top-right flex-col gap-2 rounded-[22px] bg-[var(--bar-raise)] p-4 shadow-[shadow:var(--bar-shadow)] lg:origin-top-right"
    >
      {!ask.ready ? (
        <p className="text-p-3 font-semibold">{ask.waiting}</p>
      ) : signedIn ? (
        <button
          type="button"
          onClick={() => void run()}
          disabled={busy}
          className="mm-btn sf-cta sf-cta-priced"
        >
          {busy ? 'Making it yours' : ask.label}
        </button>
      ) : (
        <InlineAuth
          id="site-bar-ask"
          source="prebuilt_page"
          inviteReply={ask.inviteReply ?? undefined}
          tone="card"
          cta={ask.label}
          onSuccess={run}
        />
      )}
      {error && <p className="text-p-3 font-semibold text-error">{error}</p>}
    </div>
  );
}
